import {
  FamilyRestroom,
  Medication,
  Share,
  MonitorHeart,
  Watch,
} from "@mui/icons-material";
import {
  Card,
  CardActionArea,
  Stack,
  Box,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import type { Palette } from "@mui/material/styles";
import type { SvgIconProps } from "@mui/material/SvgIcon";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { CannabisIcon } from "@/components/icons/CannabisIcon";

import { ControlMedications } from "./ControlMedications";

type MenuKey = "family" | "sharing" | "medications" | "fitness" | "documents" | "wearable";

/** Chaves de cor definidas em `src/configs/Theme.tsx` (palette estendida). */
type PaletteAccentKey = keyof Pick<
  Palette,
  | "secondary800"
  | "success700"
  | "error700"
  | "secondary600"
  | "primary800"
>;

interface MenuCardConfig {
  id: MenuKey;
  name: string;
  /** Uma linha que reforça o propósito daquele atalho (identidade). */
  hint: string;
  /** Ícone MUI ou imagem custom (ex.: folha de cannabis). */
  Icon: React.ComponentType<Partial<SvgIconProps>>;
  /** Cor do tema: faixa superior, ícone e borda no hover. */
  accentKey: PaletteAccentKey;
  /** Fundo suave atrás do ícone (alpha da cor de destaque). */
  iconSurfaceAlpha: number;
  /** Só o ícone, sem caixa colorida de fundo. */
  plainIcon?: boolean;
  /** Tamanho do ícone (px). Default 18. */
  iconSize?: number;
}

const MENU_ITEMS: MenuCardConfig[] = [
  {
    id: "family",
    name: "Minha família",
    hint: "Dependentes e responsáveis",
    Icon: FamilyRestroom,
    accentKey: "secondary800",
    iconSurfaceAlpha: 0.14,
  },
  {
    id: "sharing",
    name: "Compartilhamentos",
    hint: "Quem acessa seus dados",
    Icon: Share,
    accentKey: "success700",
    iconSurfaceAlpha: 0.14,
  },
  {
    id: "medications",
    name: "Medicamentos",
    hint: "Controle e lembretes",
    Icon: Medication,
    accentKey: "error700",
    iconSurfaceAlpha: 0.12,
  },
  {
    id: "fitness",
    name: "Google Health",
    hint: "Dados fitness e métricas de saúde",
    Icon: MonitorHeart,
    accentKey: "primary800",
    iconSurfaceAlpha: 0.14,
  },
  {
    id: "wearable",
    name: "Dados Wearable",
    hint: "Dados gerais do FluxHealth",
    Icon: Watch,
    accentKey: "secondary600",
    iconSurfaceAlpha: 0.12,
  },
  {
    id: "documents",
    name: "Tratamento canabinoide",
    hint: "Anvisa, receita e comprovantes",
    Icon: CannabisIcon,
    // Verde alinhado à folha de cannabis
    accentKey: "success700",
    iconSurfaceAlpha: 0,
    plainIcon: true,
    iconSize: 30,
  } 
];

interface MoreOptionsWebProps {
  onOpenFitnessData: () => void;
  /** Abre a página de medicamentos no dashboard web (em vez de Dialog interno). */
  onOpenMedications?: () => void;
}

export function MoreOptionsWeb({
  onOpenFitnessData,
  onOpenMedications,
}: MoreOptionsWebProps) {
  const theme = useTheme();
  const [openMedications, setOpenMedications] = useState<boolean>(false);

  const navigate = useNavigate();

  const handleSelect = (id: MenuKey) => {
    switch (id) {
      case "family":
        navigate("/myfamily");
        break;
      case "sharing":
        navigate("/consent");
        break;
      case "medications":
        // Web com callback do pai: página no dash. Fallback: Dialog/Screen local.
        if (onOpenMedications) {
          onOpenMedications();
        } else {
          setOpenMedications(true);
        }
        break;
      case "wearable":
        navigate("/wearableobservations");
        break;
      case "documents":
        navigate("/patient-documents");
        break;
      case "fitness":
        onOpenFitnessData();
        break;
      default:
        break;
    }
  };

  return (
    <Stack sx={{ width: "100%", minHeight: 0 }}>
      {/*
        Grid responsivo: a partir de md os 5 atalhos ficam na mesma linha.
        Em telas menores quebra em 3 → 2 → 1 sem estourar o card.
      */}
      <Box
        sx={{
          display: "grid",
          width: "100%",
          gap: { xs: 1, sm: 1.25 },
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            sm: "repeat(2, minmax(0, 1fr))",
            md: `repeat(${MENU_ITEMS.length}, minmax(0, 1fr))`,
            lg: `repeat(${MENU_ITEMS.length}, minmax(0, 1fr))`,
          },
        }}
      >
        {MENU_ITEMS.map((item) => {
          const {
            Icon,
            name,
            hint,
            accentKey,
            iconSurfaceAlpha,
            plainIcon,
            iconSize = 18,
          } = item;
          const accent = theme.palette[accentKey].main;
          return (
            <Card
              key={item.id}
              elevation={0}
              sx={{
                width: "100%",
                borderRadius: 2,
                border: "1px solid",
                borderColor: (t) => alpha(t.palette.divider, 0.9),
                backgroundColor: "background.paper",
                overflow: "hidden",
                minHeight: 0,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                /* Sem box-shadow nem scale no hover: evita “blur” visual. */
                transition: (t) =>
                  t.transitions.create(["border-color", "background-color"], {
                    duration: t.transitions.duration.shorter,
                    easing: t.transitions.easing.easeInOut,
                  }),
                "&:hover": {
                  borderColor: alpha(accent, 0.55),
                  backgroundColor: alpha(theme.palette.action.hover, 0.04),
                },
              }}
            >
              <CardActionArea
                onClick={() => handleSelect(item.id)}
                aria-label={`${name}: ${hint}`}
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                  justifyContent: "flex-start",
                  p: 0,
                  textAlign: "left",
                  minWidth: 0,
                }}
              >
                {/* Faixa de identidade por opção */}
                <Box
                  sx={{
                    height: 3,
                    width: "100%",
                    bgcolor: accent,
                  }}
                />
                <Stack
                  direction="row"
                  spacing={{ xs: 1, lg: 1.1 }}
                  alignItems="center"
                  sx={{
                    px: 1.1,
                    py: 0.75,
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      width: plainIcon ? iconSize : 32,
                      height: plainIcon ? iconSize : 32,
                      borderRadius: plainIcon ? 0 : 1.25,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      // Cannabis: só o ícone, sem caixa colorida.
                      bgcolor: plainIcon
                        ? "transparent"
                        : alpha(accent, iconSurfaceAlpha),
                      flexShrink: 0,
                    }}
                  >
                    <Icon
                      sx={{ fontSize: iconSize, color: accent }}
                      aria-hidden
                    />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="subtitle2"
                      component="h3"
                      noWrap
                      title={name}
                      sx={{
                        fontWeight: 700,
                        lineHeight: 1.15,
                        color: "text.primary",
                        fontSize: "0.75rem",
                      }}
                    >
                      {name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mt: 0.1,
                        color: "text.secondary",
                        lineHeight: 1.2,
                        fontSize: "0.625rem",
                      }}
                    >
                      {hint}
                    </Typography>
                  </Box>
                </Stack>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>

      {openMedications && !onOpenMedications && (
        <ControlMedications open={openMedications} setOpen={setOpenMedications} />
      )}
    </Stack>
  );
}
